// لوحة «المزامنة التلقائية» للعجلات.
import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAdminAuth } from "../contexts/AdminAuthContext";
import { adminApi, formatApiError } from "../lib/api";
import AdminHeader from "../components/AdminHeader";
import MythicReport from "../components/MythicReport";
import { Loader2, Play, Power, RefreshCw, AlertTriangle, CheckCircle2, PauseCircle, Mail, Send, Check } from "lucide-react";

const fmt = (iso) =>
  iso
    ? new Date(iso).toLocaleString("ar-SA", { timeZone: "Asia/Riyadh", dateStyle: "medium", timeStyle: "short" })
    : "—";

const HEALTH = {
  running: { label: "يعمل", cls: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30", Icon: CheckCircle2 },
  stopped: { label: "متوقف", cls: "text-slate-300 bg-slate-500/10 border-slate-500/30", Icon: PauseCircle },
  error: { label: "خطأ", cls: "text-red-300 bg-red-500/10 border-red-500/30", Icon: AlertTriangle },
};

const LEVEL_CLS = {
  info: "border-white/10",
  warn: "border-amber-500/30 bg-amber-500/[0.04]",
  error: "border-red-500/30 bg-red-500/[0.05]",
};

const ACTION_LABEL = {
  added: "إضافة",
  updated: "تحديث موعد",
  review: "مراجعة",
  missing: "اختفاء مؤقت",
  error: "خطأ",
  alert: "تنبيه",
  settings: "إعدادات",
  run: "فحص",
  price: "تصحيح سعر",
  price_info: "ملاحظة أسعار",
  season: "موسم",
  mythic: "تقرير الميثك",
};

const Stat = ({ label, value, testId, small }) => (
  <div className="rounded-xl bg-white/[0.03] border border-white/10 p-3">
    <div className="text-white/50 text-[11px]">{label}</div>
    <div data-testid={testId} className={`text-white font-black mt-1 tabular-nums ${small ? "text-sm" : "text-lg"}`}>{value}</div>
  </div>
);

const FILTERS = [
  { key: "all", label: "الكل" },
  { key: "added", label: "عجلات جديدة" },
  { key: "updated", label: "مواعيد العجلات" },
  { key: "price", label: "الأسعار" },
  { key: "season", label: "المواسم" },
  { key: "review", label: "مراجعة" },
  { key: "error", label: "أخطاء" },
];
const matchFilter = (log, f) =>
  f === "all" || log.action === f || (f === "price" && log.action === "price_info");

const openReviews = (logs) => logs.filter((l) => l.action === "review" && !l.resolved);

const ReviewList = ({ logs, onResolve }) =>
  openReviews(logs).length === 0 ? (
    <div data-testid="sync-review-empty" className="text-white/50 text-sm">لا توجد حالات مفتوحة.</div>
  ) : (
    <ul data-testid="sync-review-list" className="space-y-2">
      {openReviews(logs).map((l) => (
        <li key={l.id} className="rounded-xl border border-amber-500/30 bg-amber-500/[0.04] p-3 flex items-start justify-between gap-3">
          <div className="text-white/85 text-sm">
            {l.message}
            <div className="text-[11px] text-white/40 mt-1">{fmt(l.at)}</div>
          </div>
          <button type="button" data-testid={`sync-resolve-${l.id}`} onClick={() => onResolve(l.id)} className="shrink-0 inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/80 text-[11px] font-bold transition-colors">
            <Check className="w-3.5 h-3.5" /> تمت المراجعة
          </button>
        </li>
      ))}
    </ul>
  );

const DateChange = ({ log }) => {
  if (!log.old && !log.new) return null;
  if (log.new?.values) {
    return (
      <div className="mt-1.5 text-[11px] text-white/60" dir="ltr">
        <span className="line-through text-white/40">{(log.old?.values || []).join(", ")}</span>
        {" → "}
        <span className="text-[#E5CB94]">{log.new.values.join(", ")}</span>
      </div>
    );
  }
  const keys = Object.keys(log.new || log.old || {});
  return (
    <div className="mt-1.5 space-y-0.5 text-[11px] text-white/60">
      {keys.map((k) => (
        <div key={k}>
          {k === "startsAt" ? "البداية" : "النهاية"}:{" "}
          {log.old && <span className="line-through text-white/40">{fmt(log.old[k])}</span>}
          {log.old && " ← "}
          <span className="text-[#E5CB94]">{fmt(log.new?.[k])}</span>
        </div>
      ))}
    </div>
  );
};

const LogItem = ({ log }) => (
  <li data-testid={`sync-log-${log.id}`} className={`rounded-xl border p-3 ${LEVEL_CLS[log.level] || LEVEL_CLS.info}`}>
    <div className="flex items-center justify-between gap-2 text-[11px]">
      <span className="font-bold text-[#C9A96A]">{ACTION_LABEL[log.action] || log.action}</span>
      <span className="text-white/40" dir="ltr">{fmt(log.at)}</span>
    </div>
    <div className="text-white/85 text-sm mt-1">{log.message}</div>
    <DateChange log={log} />
  </li>
);

const AdminSyncPage = () => {
  const { isAuthenticated, loading: authLoading } = useAdminAuth();
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [filter, setFilter] = useState("all");
  const [report, setReport] = useState(null);

  const loadReport = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/wheel-sync/mythic-report");
      setReport(data?.report || null);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) loadReport();
  }, [isAuthenticated, loadReport]);

  const resolve = async (id) => {
    try {
      await adminApi.post(`/admin/wheel-sync/logs/${id}/resolve`);
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const load = useCallback(async () => {
    try {
      const [s, l, r] = await Promise.all([
        adminApi.get("/admin/wheel-sync/status"),
        adminApi.get("/admin/wheel-sync/logs?limit=150"),
        adminApi.get("/admin/wheel-sync/reviews"),
      ]);
      setStatus(s.data);
      setLogs(l.data?.items || []);
      setReviews(r.data?.items || []);
      setError("");
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    load();
    const t = setInterval(load, status?.in_progress ? 3000 : 20000);
    return () => clearInterval(t);
  }, [isAuthenticated, load, status?.in_progress]);

  const runNow = async () => {
    setBusy("run");
    try {
      await adminApi.post("/admin/wheel-sync/run");
      setTimeout(() => { load(); loadReport(); }, 2500);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  const toggle = async () => {
    setBusy("toggle");
    try {
      await adminApi.patch("/admin/wheel-sync/settings", { enabled: !status.enabled });
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  if (authLoading) {
    return <div className="min-h-screen bg-[#0A1128] flex items-center justify-center text-white/50 text-sm">جارٍ التحقّق من الجلسة...</div>;
  }
  if (!isAuthenticated) return <Navigate to="/admin" replace />;

  const h = HEALTH[status?.health] || HEALTH.running;
  const st = status?.last_stats || {};

  return (
    <div className="min-h-screen bg-[#0A1128] text-white" dir="rtl">
      <AdminHeader title="المزامنة التلقائية" />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-5">
        {error && (
          <div data-testid="sync-error" className="rounded-xl border border-red-500/30 bg-red-500/10 text-red-200 text-sm p-3">{error}</div>
        )}
        {!status ? (
          <div className="flex justify-center py-16 text-white/50"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <>
            <section className="rounded-2xl bg-white/[0.02] border border-white/10 p-4 sm:p-5 space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <span data-testid="sync-health" className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full border text-xs font-bold ${h.cls}`}>
                    <h.Icon className="w-3.5 h-3.5" />
                    {h.label}
                  </span>
                  {status.in_progress && (
                    <span data-testid="sync-in-progress" className="inline-flex items-center gap-1 text-[11px] text-[#E5CB94]">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> جارٍ الفحص...
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" data-testid="sync-refresh" onClick={() => { load(); loadReport(); }} className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/70 text-[11px] font-bold transition-colors">
                    <RefreshCw className="w-3.5 h-3.5" /> تحديث
                  </button>
                  <button type="button" data-testid="sync-toggle" onClick={toggle} disabled={busy === "toggle"} className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white text-[11px] font-bold transition-colors disabled:opacity-50">
                    <Power className="w-3.5 h-3.5" /> {status.enabled ? "إيقاف المزامنة" : "تشغيل المزامنة"}
                  </button>
                  <button type="button" data-testid="sync-run-now" onClick={runNow} disabled={busy === "run" || status.in_progress} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-gradient-to-b from-[#D4B278] to-[#B08A4A] text-[#0A1128] text-[11px] font-black transition hover:brightness-110 disabled:opacity-50">
                    {busy === "run" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} فحص الآن
                  </button>
                </div>
              </div>
              {status.last_error && status.health === "error" && (
                <div data-testid="sync-last-error" className="text-red-200 text-xs">آخر خطأ: {status.last_error}</div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat small label="آخر فحص" value={fmt(status.last_run_at)} testId="sync-last-run" />
                <Stat small label="الفحص القادم" value={status.enabled ? fmt(status.next_run_at) : "متوقف"} testId="sync-next-run" />
                <Stat label="عجلات مكتشفة (آخر فحص)" value={st.found ?? 0} testId="sync-found" />
                <Stat label="عجلات مرتبطة بالمتابعة" value={status.tracked} testId="sync-tracked" />
                <Stat label="أُضيفت (آخر فحص)" value={st.added ?? 0} testId="sync-added" />
                <Stat label="حُدّثت (آخر فحص)" value={st.updated ?? 0} testId="sync-updated" />
                <Stat label="إجمالي المضافة" value={status.totals?.added ?? 0} testId="sync-total-added" />
                <Stat label="إجمالي المحدّثة" value={status.totals?.updated ?? 0} testId="sync-total-updated" />
                <Stat label="أسعار صُحّحت (آخر فحص)" value={st.prices_fixed ?? 0} testId="sync-prices-fixed" />
                <Stat label="مواسم تغيّرت (آخر فحص)" value={st.seasons_changed ?? 0} testId="sync-seasons-changed" />
                <Stat label="إجمالي الأسعار المصححة" value={status.totals?.prices_fixed ?? 0} testId="sync-total-prices" />
                <Stat label="حالات تحتاج مراجعة" value={status.pending_review ?? 0} testId="sync-pending-review" />
              </div>
              <div className="flex items-center gap-4 text-[11px] text-white/60 flex-wrap">
                <span>يعمل كل {status.interval_hours} ساعات بتوقيت السعودية</span>
                <span data-testid="sync-notify-email" className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> الإيميل: {status.notifications?.email ? "مفعّل" : "غير مفعّل"}</span>
                <span data-testid="sync-notify-telegram" className="inline-flex items-center gap-1"><Send className="w-3.5 h-3.5" /> تلجرام: {status.notifications?.telegram ? "مفعّل" : "غير مربوط"}</span>
              </div>
            </section>
            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-black text-[#E5CB94]">حالات تحتاج مراجعة</h2>
              <ReviewList logs={reviews} onResolve={resolve} />
            </section>
            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-black text-[#E5CB94]">تقرير تطوير الميثك</h2>
              <div className="rounded-2xl bg-white/[0.02] border border-white/10 p-4 sm:p-5">
                <MythicReport report={report} />
              </div>
            </section>
            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-black text-[#E5CB94]">سجل التغييرات والأخطاء</h2>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {FILTERS.map((f) => (
                  <button key={f.key} type="button" data-testid={`sync-filter-${f.key}`} onClick={() => setFilter(f.key)}
                    className={`shrink-0 h-8 px-3 rounded-lg text-[11px] font-bold border transition-colors ${filter === f.key ? "bg-[#C9A96A]/15 border-[#C9A96A]/40 text-[#E5CB94]" : "bg-white/[0.02] border-white/10 text-slate-300 hover:border-[#C9A96A]/25"}`}>
                    {f.label}
                  </button>
                ))}
              </div>
              {logs.filter((l) => matchFilter(l, filter)).length === 0 ? (
                <div data-testid="sync-logs-empty" className="text-white/50 text-sm">لا يوجد سجل بعد.</div>
              ) : (
                <ul data-testid="sync-logs" className="space-y-2">{logs.filter((l) => matchFilter(l, filter)).map((l) => <LogItem key={l.id} log={l} />)}</ul>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminSyncPage;
